// @flow
import * as React from 'react';
import { type CategoryLink } from '../../types/CategoryLink';
import classNames from 'classnames';
import debounce from 'lodash/debounce';
import { FormattedMessage } from 'react-intl';
import { generateDiscoverLinks } from '../../redux/modules/discoverItems';
import { getCategoryLevel } from '../../utils/categoryGrabber';
import { getSimilarItemsUrl } from '../../utils/urls';
import isEqual from 'react-fast-compare';
import { type ItemFacets } from '../../types/ItemFacets';
import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';
import reduce from 'lodash/reduce';
import styles from './DiscoveryTags.module.scss';
import uniqBy from 'lodash/uniqBy';

const defaultTags = [
    { key: 'navigation.art', link: '/c/art/1/', title: <FormattedMessage id="navigation.art" /> },
    { key: 'navigation.jewelry', link: '/c/jewelry/6/', title: <FormattedMessage id="navigation.jewelry" /> },
    {
        key: 'navigation.asian_antiques',
        link: '/c/asian/158/',
        title: <FormattedMessage id="navigation.asian_antiques" />,
    },
    { key: 'navigation.furniture', link: '/c/furniture/4/', title: <FormattedMessage id="navigation.furniture" /> },
    {
        key: 'navigation.collectibles',
        link: '/c/collectibles/2/',
        title: <FormattedMessage id="navigation.collectibles" />,
    },
    {
        key: 'navigation.coins',
        link: '/c/coins-currency-stamps/24/',
        title: <FormattedMessage id="navigation.coins" />,
    },
    {
        key: 'navigation.memorabilia',
        link: '/c/memorabilia/26429/',
        title: <FormattedMessage id="navigation.memorabilia" />,
    },
    {
        key: 'navigation.home_and_garden',
        link: '/c/home-garden/5/',
        title: <FormattedMessage id="navigation.home_and_garden" />,
    },
    { key: 'navigation.fashion', link: '/c/fashion/3/', title: <FormattedMessage id="navigation.fashion" /> },
];

type Props = {|
    categoryLinks: CategoryLink[],
    className: string,
    facets?: ItemFacets,
    suggestions: any[],
    width: number,
|};

type State = {|
    containerWidth: number,
    left: number,
    scrollerWidth: number,
|};

export default class DiscoveryTags extends React.Component<Props, State> {
    static defaultProps = {
        categoryLinks: [],
        className: '',
        suggestions: [],
    };

    constructor(props: Props) {
        super(props);
        this.handleScroll = debounce(this.handleScroll, 10);
    }

    state = {
        containerWidth: 1000,
        left: 0,
        scrollerWidth: 1000,
    };

    componentDidMount() {
        if (this.scroller) {
            this.scroller.addEventListener('scroll', this.handleScroll);
            this.handleScroll();
            this.updateContainerWidth();
        }
    }

    shouldComponentUpdate = (nextProps: Props, nextState: State) =>
        !isEqual(this.props, nextProps) || !isEqual(this.state, nextState);

    componentDidUpdate(prevProps: Props) {
        const { facets, suggestions } = this.props;
        if (
            prevProps.width !== this.props.width ||
            !isEqual(prevProps.facets, facets) ||
            !isEqual(prevProps.suggestions, suggestions)
        ) {
            this.updateContainerWidth();
        }
    }

    componentWillUnmount() {
        if (this.scroller) {
            this.scroller.removeEventListener('scroll', this.handleScroll);
        }
    }

    handleScroll = () => {
        if (this.scroller) {
            this.setState({ left: this.scroller?.scrollLeft });
        }
    };

    updateContainerWidth = () => {
        const state = {};
        if (this.container) {
            state.containerWidth = this.container.getBoundingClientRect()?.width;
        }

        if (this.scroller) {
            state.scrollerWidth = this.getScrollerWidth();
        }
        this.setState(state);
    };

    getScrollerWidth = () => {
        return (
            reduce(
                this.scroller.children,
                (total, card) => {
                    return total + card.offsetWidth;
                },
                0
            ) +
            (this.scroller.children.length - 1) * 8
        ); // this last bit is for the margins
    };

    mapLinks = ({ key, link, title }: any) => {
        return (
            <Link className={styles.tag} key={key} route to={link}>
                {title}
            </Link>
        );
    };

    // copied and refactored from DiscoverItems.js
    createCategoryLandingLink = (mainItem: any, secondItem: any) => {
        mainItem = getCategoryLevel(mainItem);
        secondItem = getCategoryLevel(secondItem);
        const isTwoItems = secondItem.categoryUrl;

        const key = isTwoItems ? `${mainItem.categoryLabel}-${secondItem.categoryLabel}` : `${mainItem.categoryLabel}`;

        const link = isTwoItems
            ? `/c/${mainItem.categoryUrl}/${secondItem.categoryUrl}/${mainItem.categoryId}/${secondItem.categoryId}/`
            : `/c/${mainItem.categoryUrl}/${mainItem.categoryId}/`;

        const title = isTwoItems
            ? `${secondItem.categoryLabel} ${mainItem.categoryLabel}`
            : `${mainItem.categoryLabel}`;

        return { key, link, title };
    };

    mapSuggestions = ({ label: title, value }: any, index: number) => {
        const link = getSimilarItemsUrl(value);
        return { key: `${index}-${value}`, link, title };
    };

    container: any;
    scroller: any;

    render() {
        const { categoryLinks, className, facets, suggestions } = this.props;
        const { containerWidth, left, scrollerWidth } = this.state;

        const suggestionLinks = suggestions.map(this.mapSuggestions);
        const facetLinks = generateDiscoverLinks(facets, this.createCategoryLandingLink);

        let linkObjects = [...suggestionLinks, ...categoryLinks, ...facetLinks];
        if (!linkObjects.length) {
            linkObjects = defaultTags;
        }

        const tags = uniqBy(linkObjects, 'link');

        const leftFadeClass = classNames(styles.leftFade, { [styles.showFade]: left > 5 });
        const rightFadeClass = classNames(styles.rightFade, {
            [styles.showFade]: left + containerWidth + 5 < scrollerWidth,
        });

        return (
            <div className={classNames(styles.discoveryTags, className)} ref={ref => (this.container = ref)}>
                <div className={styles.tags} ref={ref => (this.scroller = ref)}>
                    {tags.map(this.mapLinks)}
                </div>
                <div className={leftFadeClass} />
                <div className={rightFadeClass} />
            </div>
        );
    }
}
